import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import type { ChangeEvent } from 'react';
import type { ColorResult } from 'react-color';
import { useMediaConfig } from './useMediaConfig';
import {
  changeDesignMobile,
  getSections,
  handleCheckBox,
  handleSearchBlur,
  handleSearchChange,
  initStates,
  transformSectionsToArray,
  aggregateProductsFromSections,
  processColorInputChange,
  processColorPickerChange,
  MAX_SLIDER_IMAGES
} from '../MobileDesignConfig.services';
import {
  MobileDesignConfigExternalProps,
  MobileDesignConfigState,
  MobileDesignProduct,
  MobileDesignSetState,
  SplashColorState,
  MobileDesignConfigView,
  MobileDesignImageKey
} from '../MobileDesignConfig.types';
import {
  TITLE_DESCRIPTION,
  CLIENT_SPACE,
  NEW_PRODUCTS_DESCRIPTION,
  LOYALTY_NAME
} from '../../../constants/dataConfig';

const INITIAL_STATE: MobileDesignConfigState = {
  isLoading: false,
  loadingCropper: false,
  name: LOYALTY_NAME,
  newProductsDescription: NEW_PRODUCTS_DESCRIPTION,
  facebook: null,
  instagram: null,
  linkedin: null,
  titleDescription: TITLE_DESCRIPTION,
  clientSpace: CLIENT_SPACE,
  products: {},
  sections: [],
  allSections: [],
  isLoadingProducts: false,
  splashBackgroundColor: '',
  url: null,
  selected: undefined
};

const INITIAL_COLOR_STATE: SplashColorState = {
  displayColorPickerSplash: false,
  newSplashColor: '',
  backgroundSplash: null,
  splashValidationMessage: null
};

interface UseMobileDesignConfigParams extends MobileDesignConfigExternalProps {
  config?: string;
  selected?: number;
  selectedProduct?: number;
}

export const useMobileDesignConfig = (props: UseMobileDesignConfigParams) => {
  const [state, setState] = useState<MobileDesignConfigState>(INITIAL_STATE);
  const [searchTerm, setSearchTerm] = useState('');
  const [colorState, setColorState] = useState<SplashColorState>(INITIAL_COLOR_STATE);
  const selectedView =
    (props?.config as MobileDesignConfigView) ?? MobileDesignConfigView.NONE;

  const { mediaState, mediaActions } = useMediaConfig({
    initialSlides: props.slides,
    initialLoyalty: props.myLoyalty as string,
    initialOrders: props.myOrders as string,
    initialProfile: props.myAccount as string
  });

  const mediaStateRef = useRef(mediaState);
  useEffect(() => {
    mediaStateRef.current = mediaState;
  }, [mediaState]);

  const mergeState = useCallback<MobileDesignSetState>((updates: Partial<MobileDesignConfigState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  useEffect(() => {
    initStates(props, mergeState);
    const initialSplashColor = props?.splashBackgroundColor || '';
    setColorState(prev => ({
      ...prev,
      newSplashColor: initialSplashColor,
      backgroundSplash: null,
      splashValidationMessage: null
    }));
    mergeState({ splashBackgroundColor: initialSplashColor || undefined });
  }, [
    mergeState,
    props?.clientSpace,
    props?.facebook,
    props?.instagram,
    props?.linkedin,
    props?.myAccount,
    props?.myLoyalty,
    props?.myOrders,
    props?.name,
    props?.newProducts,
    props?.newProductsDescription,
    props?.slides,
    props?.titleDescription,
    props?.url,
    props?.splashBackgroundColor
  ]);

  useEffect(() => {
    const fetchSections = async () => {
      const sectionsArray = transformSectionsToArray(state.sections);
      if (selectedView === MobileDesignConfigView.PRODUCTS && sectionsArray.length === 0) {
        const { sections, productShopIdMap } = await getSections(mergeState);
        mergeState({ sections, allSections: sections, productShopIdMap });
      }
    };
    fetchSections();
  }, [mergeState, selectedView, state.sections]);

  const handleSplashInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const result = processColorInputChange(event.target.value);
      setColorState(prev => ({
        ...prev,
        newSplashColor: result.normalizedValue,
        splashValidationMessage: result.validationMessage
      }));
      mergeState({ splashBackgroundColor: result.normalizedValue });
    },
    [mergeState]
  );

  const handleSplashPickerChange = useCallback(
    (color: ColorResult) => {
      const result = processColorPickerChange(color);
      setColorState(prev => ({
        ...prev,
        newSplashColor: result.hex,
        backgroundSplash: result.rgb,
        splashValidationMessage: { text: 'is-valid' }
      }));
      mergeState({ splashBackgroundColor: result.hex });
    },
    [mergeState]
  );

  const toggleSplashColorPicker = useCallback(() => {
    setColorState(prev => ({ ...prev, displayColorPickerSplash: !prev.displayColorPickerSplash }));
  }, []);

  const closeSplashColorPicker = useCallback(() => {
    setColorState(prev => ({ ...prev, displayColorPickerSplash: false }));
  }, []);

  const sectionsForDisplay = useMemo(() => transformSectionsToArray(state.sections), [state.sections]);

  const produits = useMemo(() => 
    aggregateProductsFromSections(sectionsForDisplay, state.productShopIdMap), 
    [sectionsForDisplay, state.productShopIdMap]
  );

  const handleSliderImageChange = useCallback(
    (index: number, image: string) => mediaActions.setSlide(index, image),
    [mediaActions]
  );

  const handleSliderImageRemove = useCallback(
    (index: number) => mediaActions.removeSlide(index),
    [mediaActions]
  );

  const handleUpdateDesignImage = useCallback(
    (key: MobileDesignImageKey, value: string | null) => {
      const imageActions = {
        [MobileDesignImageKey.LOYALTY]: mediaActions.setLoyaltyImage,
        [MobileDesignImageKey.PROFILE]: mediaActions.setProfileImage,
        [MobileDesignImageKey.ORDERS]: mediaActions.setOrdersImage
      };
      imageActions[key]?.(value);
    },
    [mediaActions]
  );

  const handleDesignImageUpload = useCallback(
    (image: string, key: MobileDesignImageKey) => handleUpdateDesignImage(key, image),
    [handleUpdateDesignImage]
  );

  const handleDesignImageRemove = useCallback(
    (key: MobileDesignImageKey) => handleUpdateDesignImage(key, null),
    [handleUpdateDesignImage]
  );

  const handleDisableLoader = useCallback(() => mergeState({ loadingCropper: false }), [mergeState]);

  const handleFieldChange = useCallback(<K extends keyof MobileDesignConfigState>(
    field: K,
    value: MobileDesignConfigState[K]
  ) => {
    mergeState({ [field]: value } as Partial<MobileDesignConfigState>);
  }, [mergeState]);

  const handleInputChange = useCallback(
    (field: keyof Pick<MobileDesignConfigState, 'titleDescription' | 'name' | 'newProductsDescription' | 'clientSpace'>, value: string) =>
      handleFieldChange(field, value),
    [handleFieldChange]
  );

  const handleLinkChange = useCallback(
    (field: keyof Pick<MobileDesignConfigState, 'url'>, value: string) =>
      handleFieldChange(field, value),
    [handleFieldChange]
  );

  const handleSocialChange = useCallback(
    (field: keyof Pick<MobileDesignConfigState, 'facebook' | 'instagram' | 'linkedin'>, value: string) =>
      handleFieldChange(field, value),
    [handleFieldChange]
  );

  const handleResetValue = useCallback(
    (field: keyof Pick<MobileDesignConfigState, 'titleDescription' | 'name' | 'newProductsDescription' | 'clientSpace'>, value: string) =>
      handleFieldChange(field, value),
    [handleFieldChange]
  );

  const onProductCheck = useCallback(
    (product: MobileDesignProduct) => handleCheckBox(state.products, mergeState, product),
    [mergeState, state.products]
  );

  const onSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      handleSearchChange(event, mergeState, setSearchTerm, state),
    [mergeState, state]
  );

  const applySearchFilter = useCallback(
    (term: string) => handleSearchBlur(mergeState, term, state),
    [mergeState, state]
  );

  const handleSave = useCallback(async () => {
    const processImageForSave = (img: string | null): string | null => {
      if (!img) return null;
      
      const imgString = typeof img === 'string' ? img : String(img);
      const trimmed = imgString.trim();
      if (!trimmed) return null;
      
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        return trimmed;
      }
      
      if (trimmed.startsWith('data:image')) {
        const base64 = trimmed.split(',')[1]?.trim();
        return base64 || null;
      }
      
      if (trimmed.length > 50 && /^[A-Za-z0-9+/=]+$/.test(trimmed)) {
        return trimmed;
      }
      
      return trimmed;
    };


    const processSlides = (slides: (string | null)[]): (string | null)[] => {
      return slides
        .map(slide => {
          if (slide === null || slide === undefined) {
            return null;
          }
          const slideStr = typeof slide === 'string' ? slide : String(slide);
          return processImageForSave(slideStr);
        })
        .filter((slide): slide is string => slide !== null && slide.length > 0)
        .slice(0, MAX_SLIDER_IMAGES);
    };

    const currentMediaState = mediaStateRef.current;
    const currentSlides = Array.isArray(currentMediaState.slides) ? currentMediaState.slides : [];
    const slidesToSave = processSlides(currentSlides);

    const imagesToSave = {
      pictures: slidesToSave,
      fedilityPicture: processImageForSave(currentMediaState.loyaltyImage),
      myAccountImage: processImageForSave(currentMediaState.profileImage),
      myOrdersImage: processImageForSave(currentMediaState.ordersImage)
    };

    const cleanedState = { ...state };
    const imageFieldsToExclude = [
      'fedilityPicture', 'slidesPictures', 'myOrdersImage', 'myAccountImage',
      'fedilityImage', 'slidesImages', 'myOrdersPicture', 'myAccountPicture'
    ];
    imageFieldsToExclude.forEach(field => {
      if (field in cleanedState) {
        delete (cleanedState as any)[field];
      }
    });

    await changeDesignMobile(cleanedState, produits, imagesToSave, mergeState, props);
  }, [mergeState, produits, props, state]);

  return {
    state,
    searchTerm,
    colorState,
    produits,
    sectionsForDisplay,
    setSearchTerm,
    handleSave,
    handleSliderImageChange,
    handleSliderImageRemove,
    handleDesignImageUpload,
    handleDesignImageRemove,
    handleDisableLoader,
    handleInputChange,
    handleResetValue,
    handleLinkChange,
    handleSocialChange,
    onProductCheck,
    onSearchChange,
    applySearchFilter,
    handleSplashInputChange,
    handleSplashPickerChange,
    toggleSplashColorPicker,
    closeSplashColorPicker,
    mediaState
  };
};

export type UseMobileDesignConfigReturn = ReturnType<typeof useMobileDesignConfig>;

